import { IDealRoomSettingSchema } from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useDealRoomConfig,
  useSaveDealRoomConfig,
} from '../../../../api/settingsService';
import {
  ExpandableAccordion,
  MBox,
  MFlex,
  MLink,
  MPageContainer,
  MSettingsPageHeader,
  MSkeleton,
  MText,
  MVStack,
} from '../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../components/Monetize/ExpandCollapseButton';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../../constants/settings';
import { useExpandAllControl } from '../../../../hooks';
import { useAsyncStatus } from '../../../../hooks/useAsyncStatus';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../hooks/useSettingsSectionHighlight';
import { useDocumentHead } from '../../../../services/documentHead';
import { SettingsSection } from '../../components/SettingsSection';
import { SettingsToggle } from '../../components/SettingsToggle';
import { DealRoomSettingMarketingRepository } from './components/DealRoomSettingMarketingRepository';
import { DealRoomSettingSigner } from './components/DealRoomSettingSigner';
import { DealRoomSettingWelcome } from './components/DealRoomSettingWelcome';

export const DealRoomSettings = () => {
  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Settings', 'Deal Room');
  }, []);

  const { data: dealroomConfig, isLoading } = useDealRoomConfig();
  const { mutateAsync: doSaveDealRoomConfig } = useSaveDealRoomConfig();
  const [currentConfig, setCurrentConfig] = useState(dealroomConfig);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (dealroomConfig) {
      setCurrentConfig(dealroomConfig);
    }
  }, [dealroomConfig]);

  const handleSave = async (payload: IDealRoomSettingSchema): Promise<void> => {
    try {
      setIsSaving(true);
      setCurrentConfig(payload);
      await doSaveDealRoomConfig({
        ...currentConfig,
        ...payload,
      });
    } catch (ex) {
      handleApiErrorToast(ex);
      throw ex;
    } finally {
      setIsSaving(false);
    }
  };

  const { status: enabledStatus, track: trackEnabled } = useAsyncStatus();
  const isCreationHighlighted = useSettingsSectionHighlight('dealRoomCreation');

  const {
    isAllExpanded,
    commandExpanded,
    expandCommandSeq,
    toggleExpandAll,
    onChildExpandChange,
  } = useExpandAllControl();

  return (
    <MPageContainer alignItems="stretch">
      <MBox>
        <MSettingsPageHeader divider title="Deal Room" />
      </MBox>

      <MBox w="full">
        <MVStack gap={4} maxW={SETTINGS_CONTENT_MAX_WIDTH} align="left">
          <MText>
            Deal Rooms are virtual spaces for you to share Quotes, related
            Contract documents, and more, directly with your client.
            <MLink
              href="https://docs.monetizenow.io/docs/deal-room#/"
              ml="2"
              as="a"
              target="blank"
            >
              Learn More
            </MLink>
          </MText>

          {isLoading && (
            <MVStack maxW={SETTINGS_CONTENT_MAX_WIDTH} gap={4} w="100%">
              <MSkeleton height={50} width="100%" />
              <MSkeleton height={50} width="100%" />
              <MSkeleton height={50} width="100%" />
            </MVStack>
          )}
          {!isLoading && currentConfig && (
            <>
              <SettingsSection
                id="dealRoomCreation"
                {...getHighlightStyles(isCreationHighlighted)}
                showDivider={false}
              >
                <SettingsToggle
                  name="dealRoomCreationToggle"
                  heading="Deal Room Creation"
                  subheading="Enable dealroom creation. "
                  fetchLoading={isLoading}
                  value={currentConfig.enabled}
                  asyncStatus={enabledStatus}
                  onChange={(val) =>
                    trackEnabled(() =>
                      handleSave({ ...currentConfig, enabled: val }),
                    )
                  }
                />
              </SettingsSection>
              <MFlex justifyContent="flex-start" w="full">
                <ExpandCollapseButton
                  isExpanded={isAllExpanded}
                  collapsedLabel="Expand all"
                  expandedLabel="Collapse all"
                  onChange={toggleExpandAll}
                />
              </MFlex>
              <ExpandableAccordion
                isAllExpanded={commandExpanded}
                expandCommandSeq={expandCommandSeq}
                onExpandChange={onChildExpandChange}
              >
                <DealRoomSettingWelcome
                  value={{
                    useDefaultWelcomeMessage:
                      currentConfig.useDefaultWelcomeMessage,
                    defaultWelcomeMessage: currentConfig.defaultWelcomeMessage,
                    defaultWelcomeUrl: currentConfig.defaultWelcomeUrl,
                  }}
                  isLoading={isSaving}
                  onSubmit={(val) => handleSave({ ...currentConfig, ...val })}
                  isDisabled={!currentConfig.enabled}
                />

                <DealRoomSettingMarketingRepository />

                <DealRoomSettingSigner
                  dealroomConfig={currentConfig}
                  onSubmit={(val) => handleSave({ ...currentConfig, ...val })}
                  isDisabled={!currentConfig.enabled}
                />
              </ExpandableAccordion>
            </>
          )}
        </MVStack>
      </MBox>
    </MPageContainer>
  );
};
